import type {BadgeTone} from "@/components/Layout"

// Action emitted by the packaging service when a brand's filters-propagation
// presets change. Rendered with its own sentence and a collapsible detail.
export const FILTER_PRESETS_ACTION = "update_filter_presets"

export const isFilterPresetsLog = (log: any): boolean =>
    (log?.action ?? "").toLowerCase() === FILTER_PRESETS_ACTION

// Action → badge tone (mirrors the dashboard Audit Logs colour map).
const ACTION_TONES: Record<string, BadgeTone> = {
    create: "green",
    update: "blue",
    delete: "red",
    login: "amber",
    logout: "amber",
    export: "blue",
    [FILTER_PRESETS_ACTION]: "purple",
}

export const actionTone = (action?: string): BadgeTone =>
    ACTION_TONES[(action ?? "").toLowerCase()] ?? "gray"

// Multi-word actions whose raw token would read poorly capitalised.
const ACTION_LABELS: Record<string, string> = {
    [FILTER_PRESETS_ACTION]: "Filters propagation",
}

export const formatAction = (action?: string): string => {
    if (!action) return "—"
    return ACTION_LABELS[action.toLowerCase()] ?? action.charAt(0).toUpperCase() + action.slice(1)
}

// One audit change as shipped by core's enrichment layer. Values are scalar
// strings (or null) — the audit indices reject anything else at ingest time.
export type ActivityChange = {field: string; old_value?: string | null; new_value?: string | null}

export const filterPresetsChange = (log: any): ActivityChange | null => {
    if (!Array.isArray(log?.changes)) return null
    return log.changes.find((c: any) => c?.field === "filter_presets") ?? null
}

// One preset entry parsed back from the audit rendering. `raw` is always the
// original segment so an unparseable value still shows as-is.
export type PresetDetail = {country?: string; language?: string; raw: string}

// Parses the packaging service's scalar audit value for a preset set —
// "country=FR,language=fr | language=de" — back into entries.
export const parseFilterPresetValue = (value?: string | null): PresetDetail[] => {
    if (!value || typeof value !== "string") return []
    return value
        .split("|")
        .map((segment) => segment.trim())
        .filter(Boolean)
        .map((segment) => {
            const detail: PresetDetail = {raw: segment}
            segment.split(",").forEach((pair) => {
                const [key, val] = pair.split("=").map((part) => part.trim())
                if (key === "country" && val) detail.country = val
                if (key === "language" && val) detail.language = val
            })
            return detail
        })
}

// Identity of a preset entry for diffing: country|language, or the raw
// segment when nothing parsed (so an unparseable entry still diffs as itself).
const presetKey = (p: PresetDetail): string =>
    p.country || p.language ? `${p.country ?? ""}|${p.language ?? ""}` : p.raw

export type PresetDiff = {added: PresetDetail[]; removed: PresetDetail[]; unchanged: PresetDetail[]}

// Splits a before/after pair of audit renderings into what was added, removed
// and kept — what someone reading the feed wants: "filters added or
// modified", not two lists to compare by eye. A modified line (say FR → FR+fr)
// shows as one removed and one added entry.
export const diffFilterPresets = (oldValue?: string | null, newValue?: string | null): PresetDiff => {
    const before = parseFilterPresetValue(oldValue)
    const after = parseFilterPresetValue(newValue)
    const beforeKeys = new Set(before.map(presetKey))
    const afterKeys = new Set(after.map(presetKey))
    return {
        added: after.filter((p) => !beforeKeys.has(presetKey(p))),
        removed: before.filter((p) => !afterKeys.has(presetKey(p))),
        unchanged: after.filter((p) => beforeKeys.has(presetKey(p))),
    }
}

// Headline of a filters-propagation event: the brand, or "Brand › Range" for
// a range-scoped change. `parent_label` is set by core's enrichment for range
// events; `ranges` (the app's loaded ranges) is the fallback while that is
// not deployed, since the event only carries the range's uuid.
export const filterPresetsScopeLabel = (log: any, ranges?: any[] | null): string => {
    const entity = log?.entity_label || ""
    if ((log?.entity_type ?? "").toLowerCase() !== "packaging_range") return entity
    const parent =
        log?.parent_label ||
        (Array.isArray(ranges) ? ranges.find((r: any) => r?.id === log?.entity_uuid)?.brand?.name : "") ||
        ""
    if (!parent) return entity
    return entity ? `${parent} › ${entity}` : parent
}

// Absolute date + time for event headlines ("2 Oct 2026, 14:32"); the
// relative form stays for the secondary line. `locale` is the app's i18n
// language so the headline follows the UI language, not the browser's;
// omitted (or unknown to Intl) it falls back to the browser default.
export const formatDateTime = (iso?: string, locale?: string): string => {
    if (!iso) return "—"
    const d = new Date(iso)
    if (isNaN(d.getTime())) return "—"
    try {
        return d.toLocaleString(locale || undefined, {dateStyle: "medium", timeStyle: "short"})
    } catch {
        return d.toLocaleString(undefined, {dateStyle: "medium", timeStyle: "short"})
    }
}

const ENTITY_LABELS: Record<string, string> = {qrcode: "QR code"}

export const formatEntityType = (type?: string): string => {
    if (!type) return "—"
    const key = type.toLowerCase()
    if (ENTITY_LABELS[key]) return ENTITY_LABELS[key]
    return type.split("_").filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")
}

// Compact relative time ("12 min ago", "3 h ago", "5 d ago") with a date fallback.
export const relativeTime = (iso?: string): string => {
    if (!iso) return "—"
    const d = new Date(iso)
    if (isNaN(d.getTime())) return "—"
    const diff = Date.now() - d.getTime()
    const min = Math.floor(diff / 60000)
    if (min < 1) return "just now"
    if (min < 60) return `${min} min ago`
    const h = Math.floor(min / 60)
    if (h < 24) return `${h} h ago`
    const days = Math.floor(h / 24)
    if (days < 7) return `${days} d ago`
    return d.toLocaleDateString()
}

// Initials for a user email/uuid avatar.
export const initialsFor = (value?: string): string => {
    if (!value) return "?"
    const name = value.split("@")[0]
    const parts = name.split(/[._-]/).filter(Boolean)
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
    return name.slice(0, 2).toUpperCase()
}

// Deterministic avatar colour from a string.
const AVATAR_COLORS = ["#6366F1", "#158F48", "#F59E0B", "#3B82F6", "#7C3AED", "#EC4899"]
export const avatarColor = (value?: string): string => {
    let sum = 0
    const s = value ?? ""
    for (let i = 0; i < s.length; i++) sum += s.charCodeAt(i)
    return AVATAR_COLORS[sum % AVATAR_COLORS.length]
}
